// Îlot de marché sur le terrain (D-303, D-305) : téléchargement avec une connexion, puis relevé des
// coins et recensement place par place hors connexion ; l'avancement local compte les saisies de
// l'appareil et les recensements déjà connus du serveur.
import { Bandeau, Bouton, Squelette } from '@cc/ui';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { messageErreur } from '../../api/erreurs';
import {
  avancementLocal,
  ilotEmporte,
  telechargerIlot,
  type IlotEmporte,
} from '../../terrain/ilots';
import { useEnLigne, useOperationsLocales, useSynchronisation } from '../../terrain/useTerrain';
import { Definitions, EnTeteTerrain, Section } from './commun';
import { RecensementIlot } from './RecensementIlot';
import { ReleveIlot } from './ReleveIlot';
import styles from './terrain.module.css';

export function PageIlot() {
  const { id = '' } = useParams();
  return <FicheIlot key={id} id={id} />;
}

function FicheIlot({ id }: { id: string }) {
  const [emporte, setEmporte] = useState<IlotEmporte | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const enLigne = useEnLigne();
  const sync = useSynchronisation();
  const operations = useOperationsLocales(id);
  const releveEnAttente = (operations.donnees ?? []).some(
    (o) => o.etat === 'en_attente' || o.etat === 'en_cours',
  );
  const conflit = (operations.donnees ?? []).some((o) => o.etat === 'conflit');
  const relire = useCallback(() => setRevision((r) => r + 1), []);

  useEffect(() => {
    let actif = true;
    void ilotEmporte(id)
      .then(async (local) => local ?? (enLigne ? await telechargerIlot(id) : null))
      .then((e) => {
        if (actif) setEmporte(e);
      })
      .catch((e: unknown) => {
        if (actif) setErreur(messageErreur(e));
      })
      .finally(() => {
        if (actif) setChargement(false);
      });
    return () => {
      actif = false;
    };
  }, [id, enLigne, sync.revision, revision]);

  const telecharger = async () => {
    setErreur(null);
    try {
      setEmporte(await telechargerIlot(id));
    } catch (e) {
      setErreur(messageErreur(e));
    }
  };
  const a = emporte ? avancementLocal(emporte) : null;
  return (
    <>
      <EnTeteTerrain
        titre={emporte ? `Îlot ${emporte.ilot.code}` : 'Îlot de marché'}
        sousTitre={emporte?.ilot.nom}
        retour="/terrain"
      />
      {chargement ? <Squelette lignes={4} /> : null}
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
      {!chargement && !emporte ? (
        <p>
          Cet îlot n’est pas disponible sur l’appareil. Ouvrez-le avec une connexion avant votre
          déplacement.
        </p>
      ) : null}
      {emporte && a ? (
        <>
          <Section titre="Avancement">
            <div className={styles.pile}>
              <Definitions
                elements={[
                  { terme: 'Places recensées', valeur: `${a.recensees} sur ${a.places}` },
                  {
                    terme: 'Découpage',
                    valeur: `${emporte.ilot.rangees} rangées de ${emporte.ilot.places_par_rangee} places`,
                  },
                  {
                    terme: 'Précision',
                    valeur:
                      emporte.ilot.precision === 'terrain'
                        ? 'Relevé sur le terrain'
                        : 'Schématique, à relever',
                  },
                  {
                    terme: 'Téléchargé le',
                    valeur: new Date(emporte.telecharge_le).toLocaleString('fr-FR'),
                  },
                ]}
              />
              {conflit ? (
                <Bandeau type="attention">
                  Un envoi pour cet îlot est en conflit : téléchargez à nouveau l’îlot et refaites
                  le relevé. L’envoi refusé reste dans le journal.
                </Bandeau>
              ) : null}
              <Bouton
                variante="secondaire"
                disabled={!enLigne || releveEnAttente}
                onClick={() => void telecharger()}
              >
                Télécharger à nouveau l’îlot
              </Bouton>
            </div>
          </Section>
          <ReleveIlot emporte={emporte} attente={releveEnAttente} />
          <RecensementIlot emporte={emporte} onSaisie={relire} />
        </>
      ) : null}
    </>
  );
}
